interface ImageResizeExactSizes {
    width: number;
    height: number;
}

interface ImageResizeMaxWidth {
    maxWidth: number;
}

interface ImageResizeMaxHeight {
    maxHeight: number;
}

export type ImageResizeSizes = ImageResizeExactSizes | ImageResizeMaxWidth | ImageResizeMaxHeight;

export default class AitiCoreUtils {

    scrolling: boolean = false;
    lastUniqueId: number = 0;

    constructor() {
        $j(() => {
            $j('body').on('scroll mousedown DOMMouseScroll mousewheel keyup touchmove', (e) => {
                if (e.which > 0 || e.type === 'mousedown' || e.type === 'mousewheel' || e.type === 'touchmove') {
                    $j('html, body').stop();
                    this.scrolling = false;
                }
            });
        });
    }

    mbTrim(string) {
        return string ? string.replace(/(^\s+)|(\s+$)/g, '') : '';
    }

    escapeHtml(string) {
        return $j('<div/>').text(string).html();
    }

    getUniqueId(prefix?: string): string {
        this.lastUniqueId ++;
        return prefix + this.lastUniqueId;
    }

    round(number: number, precision: number = 0): number {
        let p = Math.pow(10, precision);
        return Math.round(number * p) / p;
    }

    /**
     * @param {Array} array
     * @param {int} length
     * @returns {Array}
     */
    arrayChunk(array, length) {
        let chunks = [];

        for (let i = 0; i < array.length; i += length) {
            chunks.push(array.slice(i, i + length));
        }

        return chunks;
    }

    /**
     * @param {any} objA
     * @param {any} objB
     * @returns {boolean}
     */
    shallowEqual(objA, objB) {
        if (Object.is(objA, objB)) {
            return true;
        }
        if (
            typeof objA !== 'object' ||
            objA === null ||
            typeof objB !== 'object' ||
            objB === null
        ) {
            return false;
        }
        const keysA = Object.keys(objA);
        const keysB = Object.keys(objB);
        if (keysA.length !== keysB.length) {
            return false;
        }
        for (let i = 0; i < keysA.length; i++) {
            if (
                !objB.hasOwnProperty(keysA[i]) ||
                !Object.is(objA[keysA[i]], objB[keysA[i]])
            ) {
                return false;
            }
        }
        return true;
    }

    /**
     * @param {string} url
     * @param {function()} [fallbackCallback] - A function returns a fallback url if internal validation fails
     * @returns {JQueryDeferred}
     */
    loadScript(url: string, fallbackCallback?: Function): JQuery.Deferred<void> {
        let _this = this;

        if (fallbackCallback) {
            return jQuery.Deferred(function() {
                let deferred = this;

                _this._loadScript(url).always(function() {
                    let fallbackUrl = fallbackCallback.call(this);

                    if (fallbackUrl) {
                        _this._loadScript(fallbackUrl).done(function() {
                            deferred.resolve();
                        }).fail(function() {
                            deferred.reject();
                        });
                        return;
                    }

                    deferred.resolve();
                });
            });
        }

        return this._loadScript(url);
    }

    /**
     * @private
     * @param {string} url
     * @returns {JQueryDeferred}
     */
    _loadScript(url) {
        return jQuery.Deferred(function() {
            let deferred = this;

            if (typeof process === 'object') {
                global.loadScriptByUrl(url).then(function() {
                    deferred.resolve();
                }, function() {
                    deferred.reject();
                });
            } else {
                let script = document.createElement('script');
                script.src = url;

                script.onload = function() {
                    deferred.resolve();
                };

                script.onerror = function() {
                    deferred.reject();
                };

                document.head.appendChild(script);
            }
        });
    }

    /**
     * Scrolls to element
     *
     * @param {string|HTMLElement|JQuery<HTMLElement>} element
     * @param {int} [timeout=0]
     * @param {int} [offsetTop=20]
     * @param {boolean} [ignoreInteraction=false]
     * @param {boolean} [animation=true]
     */
    scrollToElement(element, timeout, offsetTop, ignoreInteraction, animation) {
        this.scrolling = true;

        element = $j(element);
        timeout = timeout || 0;
        offsetTop = (typeof offsetTop === 'undefined') ? 20 : offsetTop;
        ignoreInteraction = (typeof ignoreInteraction === 'undefined') ? false : ignoreInteraction;
        animation = (typeof animation === 'undefined') ? true : animation;

        setTimeout(() => {
            if (!this.scrolling && !ignoreInteraction) {
                return;
            }

            let scrollTo = element.offset().top - offsetTop;
            if (animation) {
                $j("html, body").stop().animate({
                    scrollTop: scrollTo
                }, 200);
            } else {
                $j(window).scrollTop(scrollTo);
            }
        }, timeout);
    }

    /**
     * Scrolls to element if it's not visible or partially visible
     *
     * @param {string|HTMLElement|JQuery<HTMLElement>} element
     * @param {int} [timeout=0]
     * @param {int} [offsetTop=20]
     * @param {int} [offsetBottom=20]
     * @param {boolean} [ignoreInteraction=false]
     * @param {boolean} [animation=true]
     */
    scrollToInvisibleElement(element, timeout, offsetTop, offsetBottom, ignoreInteraction, animation) {
        this.scrolling = true;

        element = $j(element);
        timeout = timeout || 0;
        offsetTop = (typeof offsetTop === 'undefined') ? 20 : offsetTop;
        offsetBottom = (typeof offsetBottom === 'undefined') ? 20 : offsetBottom;
        ignoreInteraction = (typeof ignoreInteraction === 'undefined') ? false : ignoreInteraction;
        animation = (typeof animation === 'undefined') ? true : animation;

        setTimeout(function() {
            if (!this.scrolling && !ignoreInteraction) {
                return;
            }

            let winScrollTop = $j(window).scrollTop();
            let winHeight = window.innerHeight;

            if (element.offset().top < winScrollTop || element.offset().top > winScrollTop + winHeight) {
                let scrollTo = element.offset().top - offsetTop;
                if (animation) {
                    $j("html, body").stop().animate({
                        scrollTop: scrollTo
                    }, 200);
                } else {
                    $j(window).scrollTop(scrollTo);
                }
            } else if (element.offset().top + element.outerHeight() > winScrollTop + winHeight) {
                let docHeight = $j(document).height();
                let scrollTo = winHeight < element.outerHeight() ? element.offset().top - offsetTop : winScrollTop + (element.offset().top + element.outerHeight() - (winScrollTop + winHeight)) + offsetBottom;
                if (scrollTo > docHeight - winHeight) {
                    scrollTo = docHeight - winHeight;
                }
                if (animation) {
                    $j("html, body").stop().animate({
                        scrollTop: scrollTo
                    }, 200);
                } else {
                    $j(window).scrollTop(scrollTo);
                }
            }
        }.bind(this), timeout);
    }

    resizeImage(
        image: HTMLImageElement,
        sizes?: ImageResizeSizes,
        quality: number = 100,
        type: 'image/jpeg' | 'image/png' | 'image/gif' = 'image/png',
    ): HTMLImageElement {
        let canvas: HTMLCanvasElement = document.createElement('canvas');

        canvas.width = image.width;
        canvas.height = image.height;

        if (sizes) {
            if (typeof sizes['width'] !== 'undefined') {
                canvas.width = (sizes as ImageResizeExactSizes).width;
                canvas.height = (sizes as ImageResizeExactSizes).height;
            } else {
                if (typeof sizes['maxWidth'] !== 'undefined' && canvas.width > sizes['maxWidth']) {
                    canvas.width = (sizes as ImageResizeMaxWidth).maxWidth;
                    canvas.height = canvas.width * (image.height / image.width);
                }

                if (typeof sizes['maxHeight'] !== 'undefined' && canvas.height > sizes['maxHeight']) {
                    canvas.height = (sizes as ImageResizeMaxHeight).maxHeight;
                    canvas.width = canvas.height * (image.width / image.height);
                }
            }
        }

        let ctx = canvas.getContext('2d');
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

        let newImage = new Image();
        newImage.src = canvas.toDataURL(type, quality / 100);

        return newImage;
    }
}
